import { useQueryClient } from "@tanstack/react-query"
import { Button } from "@ui-lib/index"
import { ValueLabelPair } from "@utilityTypes/ValueLabelPair"
import { requestApi2 } from "@utils/http"
import { seempSocket } from "@utils/websocket/seempSocket"
import { endpoints, SeempReport } from "api"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { getHandlePlantCacheUpdated } from "./SeempContent"
import { useSeempGetUsersQuery } from "./queries/useSeempGetUsersQuery"

export default function SeempDeleteReportBody({
	companyName,
	plantId,
	reportData,
	setModalOpenState,
}: {
	companyName: string
	plantId: number
	reportData: SeempReport
	setModalOpenState: (isOpen: boolean) => void
}) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)
	const [newName, setNewName] = useState<string>(reportData.name)
	const [newYear, setNewYear] = useState<number>(reportData.year)
	const [newResponsibleUsers, setNewResponsibleUsers] = useState<string[] | undefined>(
		reportData.responsibleUsers
	)

	const {
		isLoading: isUsersListLoading,
		isError: isUsersListError,
		data: usersList,
	} = useSeempGetUsersQuery({ companyName })

	const usersOptions = usersList?.map((user) => ({
		value: user.email,
		label: `${user.name} (${user.email})`,
	}))
	const [responsibleUsers, setResponsibleUsers] = useState<ValueLabelPair[]>([])

	useEffect(() => {
		if (
			usersOptions === undefined ||
			usersOptions === null ||
			reportData.responsibleUsers === undefined ||
			reportData.responsibleUsers === null
		)
			return

		const originalUsers: ValueLabelPair[] | [] = usersOptions
			.filter((user) => reportData.responsibleUsers?.includes(user.value) == true)
			.map((user) => user)

		setResponsibleUsers(originalUsers)
	}, [usersList])

	const [confirmDelete, setConfirmDelete] = useState<boolean>(false)

	const handleDeleteReport = async () => {
		if (companyName === undefined) {
			alert(t("ERROR: THE COMPANY NAME IS NOT DEFINED."))
			return
		}

		{
			const response = await requestApi2(endpoints.seempDeleteReport, {
				id: reportData.id,
			})
			if (response?.success === true) {
				alert(t("REPORT DELETED SUCCESSFULLY"))
				seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
				seempSocket.sendPlantCacheUpdateRequest(
					companyName,
					plantId,
					"delete",
					undefined,
					reportData.id
				)
				handlePlantCacheUpdated({
					plantId,
					updateType: "delete",
					data: undefined,
					reportId: reportData.id,
				})
			} else {
				alert(t("FAILED TO DELETE REPORT"))
			}
		}
	}

	return (
		<div
			className={`shadow-card fixed inset-1/2 m-4 flex h-80 w-100 -translate-1/2 flex-col items-center justify-items-start rounded-md border-0 bg-white`}
		>
			<div
				className={`absolute top-0 right-0 left-0 flex w-full flex-col items-center gap-3 p-8`}
			>
				<div className="">
					<p
						className={
							"text-dipai-secondary-901 text-title3 h-8 text-center font-semibold"
						}
					>
						{t("DELETE SEEMP REPORT")}
					</p>
				</div>

				<div className="mb-4 flex w-full flex-row items-center justify-center">
					<label
						htmlFor="report-name"
						className="text-menu h-4 text-center"
					>
						{`${t("REPORT NAME")}: ${reportData.name}`}
					</label>
				</div>

				<div className="mb-4 flex w-full flex-row items-center justify-center gap-2">
					<label
						htmlFor="report-name"
						className="text-menu mb-8 h-4 text-center"
					>
						{`${t("ARE YOU SURE YOU WANT TO DELETE THIS REPORT")}`}
					</label>
				</div>

				<div className="mb-4 flex w-full items-center justify-center gap-3">
					<input
						type="checkbox"
						id="confirm-delete"
						name="confirm-delete"
						checked={confirmDelete}
						onChange={() => setConfirmDelete(!confirmDelete)}
					/>
					<label
						htmlFor="confirm-delete"
						className="text-menu"
					>
						{t("YES, I WANT TO DELETE THIS REPORT")}
					</label>
				</div>

				<div className="flex w-full flex-row justify-center gap-3">
					<Button
						variant={"outline-dark"}
						onClick={() => setModalOpenState(false)}
					>
						<Button.Label>{t("CANCEL")}</Button.Label>
					</Button>
					<Button
						variant={"filled-secondary"}
						className="bg-red-600 text-white hover:bg-red-900"
						disabled={confirmDelete === false}
						onClick={() => handleDeleteReport()}
					>
						<Button.Label>{t("DELETE")}</Button.Label>
					</Button>
				</div>
			</div>
		</div>
	)
}
